import { useCallback, useMemo, useState } from 'react'
import { App, Button, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ApiOutlined, PlusOutlined, UnlockOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PageHeader } from '@/shared/ui/PageHeader'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { utcTooltip } from '@/shared/lib/format'
import { relativeFromNow } from '@/features/session/relativeTime'
import { DeviceClockSkew } from '@/features/attendance/DeviceClockSkew'
import { DeviceConnectionInfoModal } from '@/features/attendance/DeviceConnectionInfoModal'
import { DeviceConnectionTag } from '@/features/attendance/DeviceConnectionTag'
import { DeviceDirectionTag } from '@/features/attendance/DeviceDirectionTag'
import { DeviceGatewayIndex } from '@/features/attendance/DeviceGatewayIndex'
import { DeviceFormModal } from '@/features/attendance/DeviceFormModal'
import { DeviceProbeModal } from '@/features/attendance/DeviceProbeModal'
import { DeviceStatusTag } from '@/features/attendance/DeviceStatusTag'
import { localizeOutcome } from '@/features/attendance/outcomeText'
import { SiteRef } from '@/features/sites/SiteRef'
import {
  configureWebhook,
  deleteDevice,
  fetchDevicePage,
  formatWebhookAddress,
  openDoor,
  probeDevice,
  type AttendanceDevice,
  type DeviceProbeResult,
} from '@/features/attendance/attendanceApi'
import type { OperationResult } from '@/shared/api/operationResult'

/**
 * Davomat terminallari (Hikvision DS-K1T343MFWX) reyestri.
 *
 * <h2>Nima uchun bu sahifa birinchi bo'lib qilindi</h2>
 * Davomat bo'limining qolgan uchta sahifasi (xodimlar, hodisalar, hisobot)
 * shu ro'yxatga TAYANADI: xodimni qurilmaga yuborish ham, hodisa qaysi
 * eshikdan kelganini bilish ham qurilma yozuvisiz mumkin emas. Va eng
 * muhimi — hisobotning to'g'riligi shu yerdagi ikki ustunga bog'liq:
 * holat va soat farqi.
 *
 * <h2>⚠️ HOLAT USTUNIDA TO'RT QIYMAT, UCHTA EMAS</h2>
 * `AUTH_FAILED` `UNREACHABLE` dan ATAYLAB ajratilgan. Sabab
 * `DeviceStatusTag` da batafsil, qisqasi: birinchisida qurilma TIRIK va
 * tuzatish shu yerdagi formada, ikkinchisida esa serverxonaga borish
 * kerak. Ularni birlashtirish operatorni butunlay boshqa yo'nalishda
 * qidirishga majbur qilardi.
 *
 * <h2>⚠️ SOAT FARQI — SAHIFANING ENG QIMMATLI USTUNI</h2>
 * 2026-08-11 da haqiqiy qurilma Toshkent vaqtini ko'rsatib turib, uni
 * `+08:00` deb e'lon qilgan: har hodisa 3 soatga surilgan holda yozilardi
 * va buni HECH KIM sezmasdi (`DeviceClockSkew` sarlavhasi). Bu ustun
 * aynan shuning uchun bor va aynan shuning uchun jadvalda, kartochka
 * ichida emas.
 *
 * <h2>Nega `DataTable`</h2>
 * Ro'yxat SERVER TOMONDA sahifalanadi va qidiriladi (`DeviceQuery`:
 * `search`, `page`, `size`), `DevicePage` esa haqiqiy `total` beradi —
 * ya'ni jadval sahifalar sonini taxmin qilmaydi va `showTotal` yoqilgan
 * holicha qoladi (`NasPage` bilan bir xil holat).
 *
 * <h2>⚠️ SARALASH QO'YILMAGAN</h2>
 * Backend saralashni qabul qilmaydi. Ustunlarga `sorter` qo'yish faqat
 * JORIY sahifani tartiblab, "eng yomon holat yuqorida" degan yolg'on
 * va'da berardi — ikkinchi sahifadagi o'lik qurilma baribir ko'rinmasdi.
 */
export function AttendanceDevicesPage() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()

  const [editing, setEditing] = useState<AttendanceDevice | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [result, setResult] = useState<OperationResult | null>(null)

  /*
    ⚠️ Probe natijasi MODALDA, jadval katagida emas — sabab
    `DeviceProbeModal` sarlavhasida. Yugurayotgan qator alohida
    saqlanadi: "yuklanmoqda" belgisi FAQAT bosilgan tugmada turishi kerak,
    aks holda bitta tekshiruv butun ustunni band qilib ko'rsatardi.
  */
  const [probing, setProbing] = useState<string | null>(null)
  /*
    ⚠️ QATORNING O'ZI saqlanadi, faqat nomi emas: natija oynasida
    gateway ma'lumoti ham ko'rsatiladi (`gatewayDevIndex`) va u probe
    javobida YO'Q — u qurilma yozuvida turadi.
  */
  const [probe, setProbe] = useState<{
    device: AttendanceDevice
    result: DeviceProbeResult
  } | null>(null)

  /*
    ⚠️ Webhook yozish uchun alohida "yugurayotgan qator" — `probing` bilan
    bir xil sabab: ikkalasi ham tarmoq amali va ikkalasi ham sekin
    bo'lishi mumkin. Bitta bayroqni ikkalasiga ishlatish "tekshirish"
    tugmasini bosganda "webhook" tugmasini ham aylantirib ko'rsatardi.
  */
  const [writingWebhook, setWritingWebhook] = useState<string | null>(null)

  /*
    ⚠️ «Ulash ma'lumotlari» — kamera menyusiga TERILADIGAN to'rtta qiymat.
    Ikki yo'ldan ochiladi va ikkalasi ham kerak:
      · qurilma saqlangan zahoti AVTOMATIK (aks holda yaratilgan, lekin
        hech kim ulamagan qurilma qolib ketardi);
      · keyin istalgan paytda qator amalidan (texnik ikkinchi marta
        kelganda yoki qiymat yo'qolganda).
  */
  const [connectionInfo, setConnectionInfo] = useState<AttendanceDevice | null>(null)

  // ⚠️ `useCallback` — bu funksiyalar `useMemo` ichidagi ustun
  // ta'riflariga tushadi va ular eskirgan nusxani ushlab qolmasligi
  // kerak (`NasPage` da eslint aynan shuni ogohlantirgan).
  const runProbe = useCallback(
    (device: AttendanceDevice) => {
      setProbing(device.id)
      probeDevice(device.id)
        .then((outcome) => {
          setProbe({ device, result: outcome })
          // Probe SERVERDA `status`/`lastSeenAt`/`clockSkewSeconds` ni
          // yangilaydi — ro'yxat ham eskiradi. Buni unutish "tekshirdim,
          // lekin jadvalda eski holat" degan ziddiyat qoldirardi.
          void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
        })
        .catch((err: unknown) =>
          // Natija HAR HOLDA ko'rsatiladi: jimgina hech narsa bo'lmasligi
          // "tugma ishlamadi" bo'lib o'qilardi. Serverga umuman yetib
          // bormaganimiz `UNKNOWN` — bu qurilma haqida emas, biz haqimizda.
          setProbe({
            device,
            result: {
              status: 'UNKNOWN',
              detail: translateError(err, t),
              deviceInfo: null,
              deviceLocalTime: null,
              deviceTimeZoneRaw: null,
              clockSkewSeconds: null,
              probedAt: null,
            },
          }),
        )
        .finally(() => setProbing(null))
    },
    [t, queryClient],
  )

  const confirmDelete = useCallback(
    (device: AttendanceDevice) => {
      modal.confirm({
        title: t('attendance.deleteTitle'),
        content: (
          <Space direction="vertical" size={4}>
            <Typography.Text>
              {t('attendance.deleteSubject', { name: device.name, deviceId: device.deviceId })}
            </Typography.Text>
            {/* ⚠️ Oqibat OCHIQ aytiladi: yozuv bilan birga o'sha
                qurilmadan kelgan HODISALAR ham ketadi (`event_device_fk`
                → `ON DELETE CASCADE`). Ya'ni bu "ro'yxatdan olib
                tashlash" emas, davomat tarixining bir bo'lagini
                yo'qotish. Qurilma vaqtincha ishlamayotgan bo'lsa
                to'g'ri amal — o'chirish emas, "Faol" bayrog'ini
                o'chirish. */}
            <Typography.Text type="secondary">{t('attendance.deleteWarn')}</Typography.Text>
          </Space>
        ),
        okText: t('attendance.delete'),
        cancelText: t('common.cancel'),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            const outcome = localizeOutcome(await deleteDevice(device.id), 'deleteDevice', t)
            void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
            if (outcome.status === 'OK') message.success(outcome.message)
            else setResult(outcome)
          } catch (err) {
            setResult({ status: 'FAILED', message: translateError(err, t) })
          }
        },
      })
    },
    [modal, message, queryClient, t],
  )

  const confirmOpenDoor = useCallback(
    (device: AttendanceDevice) => {
      modal.confirm({
        title: t('attendance.openDoorTitle'),
        content: (
          <Space direction="vertical" size={4}>
            <Typography.Text>
              {t('attendance.openDoorSubject', { name: device.name })}
            </Typography.Text>
            {/* ⚠️ TASDIQLASH MAJBURIY va bu shakliy emas: tugma HAQIQIY
                eshikni ochadi. Tasodifan bosilgan tugma binoni bir
                necha soniyaga qulfsiz qoldiradi va bu jurnalda faqat
                keyin ko'rinadi. */}
            <Typography.Text type="secondary">{t('attendance.openDoorWarn')}</Typography.Text>
          </Space>
        ),
        okText: t('attendance.openDoor'),
        cancelText: t('common.cancel'),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            const outcome = localizeOutcome(await openDoor(device.id), 'openDoor', t)
            // ⚠️ `OperationResult`, boolean EMAS. Chaqiruv ketgani
            // "eshik ochildi" degani emas — qurilma javob bermasligi
            // yoki rad etishi mumkin. `OK` bo'lmagan har qanday natija
            // ekranda QOLADI, "muvaffaqiyatli" deb yashirilmaydi.
            if (outcome.status === 'OK') message.success(outcome.message)
            else setResult(outcome)
          } catch (err) {
            setResult({ status: 'FAILED', message: translateError(err, t) })
          }
        },
      })
    },
    [modal, message, t],
  )

  /**
   * Webhook manzilini QURILMAGA yozish.
   *
   * ⚠️ Tasdiqlash so'raladi va bu shakliy emas: amal qurilmaning
   * SOZLAMASINI o'zgartiradi va o'sha uyada turgan boshqa integratsiya
   * (mijozning o'z tizimi) ustiga yozilishi mumkin.
   *
   * ⚠️ Natija HAR HOLDA ekranda qoladi: `PARTIAL` — sariq va u haqiqiy
   * holat ("qurilma qabul qildi, lekin tasdiqlanmadi"). Uni yashil deb
   * ko'rsatish CLAUDE.md §3.2, Qoida 4 ni buzardi.
   */
  const confirmWriteWebhook = useCallback(
    (device: AttendanceDevice) => {
      const address = formatWebhookAddress(device.webhookHost, device.webhookPort)
      modal.confirm({
        title: t('attendance.writeWebhookTitle'),
        content: (
          <Space direction="vertical" size={4}>
            <Typography.Text>
              {t('attendance.writeWebhookSubject', { name: device.name })}
            </Typography.Text>
            <Typography.Text type="secondary">
              {address === ''
                ? t('attendance.writeWebhookGlobal')
                : t('attendance.writeWebhookOwn', { address })}
            </Typography.Text>
            <Typography.Text type="secondary">{t('attendance.writeWebhookWarn')}</Typography.Text>
          </Space>
        ),
        okText: t('attendance.writeWebhook'),
        cancelText: t('common.cancel'),
        onOk: async () => {
          setWritingWebhook(device.id)
          try {
            const outcome = localizeOutcome(await configureWebhook(device.id), 'writeWebhook', t)
            if (outcome.status === 'OK') message.success(outcome.message)
            else setResult(outcome)
          } catch (err) {
            setResult({ status: 'FAILED', message: translateError(err, t) })
          } finally {
            setWritingWebhook(null)
          }
        },
      })
    },
    [modal, message, t],
  )

  const columns = useMemo<ColumnsType<AttendanceDevice>>(
    () => [
      {
        key: 'name',
        title: t('attendance.name'),
        width: 200,
        render: (_, row) => (
          <Space size={6}>
            <Typography.Text strong>{row.name}</Typography.Text>
            {/* Nofaol qurilma so'ralmaydi — buni ko'rsatmaslik "nega
                holat yangilanmayapti?" degan savolni javobsiz qoldirardi */}
            {row.active ? null : <Tag>{t('attendance.inactive')}</Tag>}
          </Space>
        ),
      },
      {
        key: 'deviceId',
        title: t('attendance.deviceId'),
        width: 150,
        render: (_, row) => <Typography.Text code>{row.deviceId}</Typography.Text>,
      },
      {
        /*
          ⚠️ YO'NALISH USTUNI BELGIDAN KEYIN, MANZILDAN OLDIN — ya'ni
          "bu qaysi qurilma" savolining bir qismi sifatida. U
          sozlamalar oxiriga qo'yilsa gorizontal scroll ortida qolardi,
          holbuki hisobotning kelish/ketish ustunlari AYNAN shu qiymatga
          bog'liq: `BOTH` bo'lgan terminalda ular o'lchov emas, taxmin
          bo'ladi.
        */
        key: 'direction',
        title: t('attendance.direction'),
        width: 130,
        render: (_, row) => <DeviceDirectionTag direction={row.direction} />,
      },
      {
        /*
          ⚠️ ULANISH TURI MANZILDAN OLDIN — chunki u manzil katagini
          QANDAY o'qishni aytadi. Keyin qo'yilsa operator `AB1234567` ni
          ko'rib "bu qanaqa IP?" deb o'ylardi.

          ⚠️ USTUN HAR DOIM CHIZILADI, faqat gateway qurilmalari bo'lganda
          emas: ustunning paydo bo'lib-yo'qolishi jadval tuzilishini
          ma'lumotga bog'lab qo'yardi va operator saqlab qo'ygan ustun
          tartibi (`tableId`) buzilardi.
        */
        key: 'connectionType',
        title: t('attendance.connectionType'),
        width: 150,
        render: (_, row) => <DeviceConnectionTag connectionType={row.connectionType} />,
      },
      {
        key: 'address',
        title: t('attendance.address'),
        width: 210,
        render: (_, row) => {
          /*
            ⚠️ IKKI REJIM — IKKI XIL MANZIL, va ular bir xil katakda
            turadi. Sabab: savol bitta ("bu qurilmaga qanday boriladi"),
            javob esa rejimga qarab boshqacha. Ikkita alohida ustun
            qilish har qatorda bittasini bo'sh qoldirardi va jadval
            "to'ldirilmagan" ko'rinardi.
          */
          if (row.connectionType === 'ISUP_GATEWAY') {
            return (
              <Space direction="vertical" size={0}>
                {/* Gateway'dagi identifikator — operator KIRITGAN qiymat */}
                {row.isupDeviceId === null ? (
                  <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
                ) : (
                  <Typography.Text code copyable={{ text: row.isupDeviceId }}>
                    {row.isupDeviceId}
                  </Typography.Text>
                )}
                {/* ⚠️ SERVER topgan o'rin. Bo'shligi NOSOZLIK EMAS —
                    `DeviceGatewayIndex` sarlavhasidagi izoh. */}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('attendance.gatewayDevIndex')}: <DeviceGatewayIndex devIndex={row.gatewayDevIndex} />
                </Typography.Text>
              </Space>
            )
          }
          /*
            ⚠️ `DIRECT_ISAPI` KATAGI O'ZGARMADI — aynan eski ko'rinish
            (`ip:port`, nusxalanadigan). Bu rejim bugun ishlab turibdi va
            uning ekrandagi xulqiga tegilmaydi.

            `?? ''` — sxema bo'yicha bu maydonlar `null` bo'lishi mumkin,
            lekin `DIRECT_ISAPI` da server ularni har doim beradi. Shunga
            qaramay kod `null` ni "null:null" deb chizmasin.
          */
          const address = `${row.ipAddress ?? ''}:${row.port ?? ''}`
          return (
            <Typography.Text code copyable={{ text: address }}>
              {address}
            </Typography.Text>
          )
        },
      },
      {
        /*
          ⚠️ JOYLASHUV USTUNI MANZILDAN KEYIN, HOLATDAN OLDIN — ya'ni
          "nima ishlamayapti" savolining javobi bilan bir joyda. Sabab
          o'lchangan xulqda: `UNREACHABLE` qatorini ko'rgan operatorning
          KEYINGI savoli har doim "bu terminal qayerda turibdi" bo'ladi,
          va shu paytgacha panelda bu javob HECH QAYERDA yo'q edi —
          maydon ham, ustun ham chizilmasdi.

          ⚠️ `SiteRef` — bo'sh katak HECH QACHON chizmaydi: "biriktirilmagan",
          "yuklanmoqda", "huquq yo'q" va "topilmadi" to'rt xil ko'rinadi
          (`SiteRef` sarlavhasi). Bu ustunda aynan shu kerak, chunki
          bo'sh katak ma'lumot sifatidagi nuqsonni yashirardi.
        */
        key: 'site',
        title: t('attendance.site'),
        width: 190,
        render: (_, row) => <SiteRef siteId={row.siteId} showRegion={false} />,
      },
      {
        // ⚠️ Holat ATAYLAB manzildan keyin, sozlamalardan oldin: sahifa
        // ochilishining eng ko'p uchraydigan sababi "nima ishlamayapti"
        // degan savol. Oxiriga qo'yilsa u gorizontal scroll ortida
        // qolardi (`NasPage` bilan bir xil mulohaza).
        key: 'status',
        title: t('attendance.statusColumn'),
        width: 170,
        render: (_, row) => (
          <DeviceStatusTag
            status={row.status}
            lastError={row.lastError}
            consecutiveFailures={row.consecutiveFailures}
          />
        ),
      },
      {
        key: 'lastSeen',
        title: t('attendance.lastSeen'),
        width: 160,
        render: (_, row) =>
          row.lastSeenAt === null ? (
            // ⚠️ "Hech qachon" — bo'sh katak EMAS. Bo'sh katak "ma'lumot
            // yuklanmadi" bo'lib o'qilardi, holbuki bu aniq fakt:
            // qurilma bizga bironta ham muvaffaqiyatli javob bermagan.
            <Tooltip title={t('attendance.lastSeenNeverHint')}>
              <Typography.Text type="secondary">{t('attendance.lastSeenNever')}</Typography.Text>
            </Tooltip>
          ) : (
            // Nisbiy vaqt — javobning O'ZI ("bu qurilma hali tirikmi?").
            // Aniq vaqt yo'qolmaydi: u tooltip'da, tenant zonasi va UTC
            // bilan birga (`relativeTime.ts` dagi bir xil qoida).
            <Tooltip title={utcTooltip(row.lastSeenAt)}>
              <Typography.Text>{relativeFromNow(row.lastSeenAt, t)}</Typography.Text>
            </Tooltip>
          ),
      },
      {
        key: 'clockSkew',
        title: t('attendance.clockSkew'),
        width: 150,
        render: (_, row) => (
          <DeviceClockSkew seconds={row.clockSkewSeconds} timeZone={row.timeZone} />
        ),
      },
      {
        key: 'actions',
        title: '',
        // Amallar bitta «uch nuqta» menyusiga yig'ildi: ilgari olti tugma
        // 610px egallab, jadval ma'lumotini siqib qo'yardi.
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          // Huquq va shartlar `RowActions` ichida filtrlanadi — mantiq
          // o'zgarmadi:
          //   · «Tekshirish» — diagnostika, huquq TALAB QILINMAYDI;
          //   · «Ulash ma'lumotlari» — faqat `ISUP_GATEWAY` qatorida;
          //   · «Eshik ochish» — alohida huquq va tasdiqlash bilan.
          <RowActions
            items={[
              {
                key: 'probe',
                label: t('attendance.probe'),
                onClick: () => runProbe(row),
                loading: probing === row.id,
              },
              {
                key: 'edit',
                label: t('attendance.edit'),
                onClick: () => {
                  setEditing(row)
                  setFormOpen(true)
                },
                permission: 'attendance.device.write',
              },
              {
                key: 'connectionInfo',
                label: t('attendance.connectionInfo'),
                onClick: () => setConnectionInfo(row),
                permission: 'attendance.device.write',
                hidden: row.connectionType !== 'ISUP_GATEWAY',
              },
              {
                key: 'writeWebhook',
                label: t('attendance.writeWebhook'),
                icon: <ApiOutlined />,
                onClick: () => confirmWriteWebhook(row),
                permission: 'attendance.device.write',
                loading: writingWebhook === row.id,
              },
              {
                key: 'openDoor',
                label: t('attendance.openDoor'),
                icon: <UnlockOutlined />,
                onClick: () => confirmOpenDoor(row),
                permission: 'attendance.door.open',
                danger: true,
                divider: true,
              },
              {
                key: 'delete',
                label: t('attendance.delete'),
                onClick: () => confirmDelete(row),
                permission: 'attendance.device.write',
                danger: true,
                divider: true,
              },
            ]}
          />
        ),
      },
    ],
    [t, probing, writingWebhook, runProbe, confirmDelete, confirmOpenDoor, confirmWriteWebhook],
  )

  return (
    <>
      <PageHeader
        title={t('nav.attendanceDevices')}
        description={t('pageIntro.attendanceDevices')}
        extra={
          <Can permission="attendance.device.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('attendance.createTitle')}
            </Button>
          </Can>
        }
      />

      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={result}
            closable
            onClose={() => setResult(null)}
          />
        </div>
      ) : null}

      <DataTable<AttendanceDevice>
        queryKey={queryKeys.attendance.devices}
        fetchPage={fetchDevicePage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="attendance-devices"
        /* ⚠️ Ulanish turi ustuni (+150) va kengaygan manzil ustuni (+20)
           qo'shilgach oshirildi: sig'masa oxirgi ustunlar kesilib qolardi */
        // Amallar menyuga yig'ilgach ustunlar 1930 dan 1432 ga tushdi;
        // eski qiymat ~500px bekor gorizontal scroll berardi.
        // ⚠️ +190: joylashuv ustuni qo'shildi (200+150+130+150+210+190+170+160+150+56 = 1566)
        scrollX={1570}
        stickyFirstColumn
        searchable
        searchPlaceholder={t('attendance.searchPlaceholder')}
        emptyTitle={t('attendance.empty')}
        emptyDescription={t('attendance.emptyHint')}
        /*
          ⚠️ 60 soniya — fon jarayoni qurilmalarni taxminan shu davrda
          so'raydi. Undan tez yangilash YANGI MA'LUMOT BERMAYDI: bazadagi
          `status` o'zgarmagan bo'ladi. Panelning o'zi qurilmaga HECH
          QACHON to'g'ridan-to'g'ri murojaat qilmaydi — buni faqat
          "Tekshirish" tugmasi qiladi.

          ⚠️ `NasPage` da ro'yxat avtomatik yangilanmaydi va bu farq
          ataylab: u yerda holat ALOHIDA so'rovda keladi, bu yerda esa
          holat ro'yxatning O'ZIDA. Ya'ni bu jadvalni muzlatib qo'yish
          "hammasi joyida" degan eskirgan manzarani ekranda qoldirardi.
        */
      />

      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
        {t('attendance.statusSourceHint')}
      </Typography.Paragraph>

      <DeviceFormModal
        open={formOpen}
        device={editing}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
        /* ⚠️ Faqat gateway qurilmasida — `DIRECT_ISAPI` da terib
           beriladigan qiymat yo'q va oyna bo'sh chiqardi. Bu shart aynan
           shu rejim xulqiga tegmasligining bir qismi. */
        onSaved={(saved, outcome) => {
          /*
            ⚠️ `PARTIAL` SAHIFADA ko'rsatiladi (2026-08-15, D4).

            `ISUP_GATEWAY → DIRECT_ISAPI` o'tishida gateway'dan o'chirish
            yiqilishi mumkin: bizda qator yangilanadi, gateway ro'yxatida
            esa YETIM yozuv qoladi. Ilgari `PUT` javobi qurilmaning o'zi
            edi va bu holat panelga UMUMAN yetib kelmasdi — foydalanuvchi
            yashil «saqlandi» xabarini olardi (`CLAUDE.md` §3.2, 4-qoida:
            «hech qachon muvaffaqiyatli deb yolg'on aytmang»).

            ⚠️ Tartib boshqa amallardagidek: `OK` — o'tib ketadigan yashil
            xabar, qolgani — YOPILGUNCHA turadigan sariq banner. Sabab
            `deleteMutation` izohida: ogohlantirishni `message` bilan
            ko'rsatish uni 3 soniyada yo'qotardi.
          */
          if (outcome && outcome.status !== 'OK') setResult(localizeOutcome(outcome, 'saveDevice', t))
          if (saved.connectionType === 'ISUP_GATEWAY') setConnectionInfo(saved)
        }}
      />

      <DeviceConnectionInfoModal
        device={connectionInfo}
        onClose={() => setConnectionInfo(null)}
      />

      <DeviceProbeModal
        result={probe?.result ?? null}
        device={probe?.device ?? null}
        onClose={() => setProbe(null)}
      />
    </>
  )
}
